import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Activity, Info, Layers } from 'lucide-react';
import { formatMessagesAnalyzed, getPatternSentence, type RiskLevel } from '../../../utils/scanReport';
interface ForensicResultHeaderProps {
  score: number;
  riskLevel: RiskLevel;
  patternCount: number;
  messageCount: number | null;
  title?: string | null;
  createdAt?: string;
  /** AI summary — only passed for full-access reports. */
  summary?: string | null;
}
const RISK_STYLES: Record<RiskLevel, {
  label: string;
  text: string;
  chip: string;
  bar: string;
  dot: string;
}> = {
  high: {
    label: 'High Risk',
    text: 'text-forensic-crimson-soft',
    chip: 'border-forensic-crimson/40 bg-forensic-crimson/10',
    bar: 'bg-forensic-crimson',
    dot: 'bg-forensic-crimson'
  },
  medium: {
    label: 'Medium Risk',
    text: 'text-amber-300',
    chip: 'border-amber-400/30 bg-amber-400/[0.08]',
    bar: 'bg-amber-400',
    dot: 'bg-amber-400'
  },
  low: {
    label: 'Low Risk',
    text: 'text-emerald-300',
    chip: 'border-emerald-400/30 bg-emerald-400/[0.08]',
    bar: 'bg-emerald-400',
    dot: 'bg-emerald-400'
  }
};
export function ForensicResultHeader({
  score,
  riskLevel,
  patternCount,
  messageCount,
  title,
  createdAt,
  summary
}: ForensicResultHeaderProps) {
  const reduceMotion = useReducedMotion();
  const risk = RISK_STYLES[riskLevel];
  const clamped = Math.max(0, Math.min(100, score));
  return <section aria-labelledby="result-heading" className="rounded-2xl border border-forensic-line bg-forensic-surface" data-id="element-208" data-mp="23xbh7cdqnrgr">
      <div className="grid gap-8 p-6 sm:p-10 md:grid-cols-[minmax(0,240px)_1fr] md:items-center md:gap-12" data-id="element-209" data-mp="2dty1o025kgi9">
        {/* Score — the hero */}
        <div data-id="element-210" data-mp="28ta4ewnbz3fl">
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-forensic-subtle" data-id="element-211" data-mp="28wxutmxhb9q3">
            Forensic Analysis
          </p>
          <div className="mt-4 flex items-baseline gap-2" data-id="element-212" data-mp="3kajit4rlr5a1">
            <span className="text-[88px] font-semibold leading-[0.85] tracking-tight text-white tabular-nums sm:text-[112px]" data-id="element-213" data-mp="3kc4bspy9qg5s">
              {clamped}
            </span>
            <span className="text-base text-forensic-subtle" data-id="element-214" data-mp="52jdnjqcgfl">/100</span>
          </div>
          <span className={`mt-5 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold tracking-[0.12em] ${risk.chip} ${risk.text}`} data-id="element-215" data-mp="571keqrzz19d">
            <span className={`h-1.5 w-1.5 rounded-full ${risk.dot}`} aria-hidden="true" data-id="element-216" data-mp="38ni2dnm3hh80" />
            {risk.label.toUpperCase()}
          </span>
          <p className="mt-2 text-sm text-forensic-muted" data-id="element-217" data-mp="3dj7705cz1ob6">Toxicity Score</p>
        </div>

        {/* Narrative */}
        <div className="min-w-0 border-t border-forensic-line pt-8 md:border-l md:border-t-0 md:pl-12 md:pt-0" data-id="element-218" data-mp="3nbcxtbrhlzos">
          <p className="truncate text-xs text-forensic-subtle" data-id="element-219" data-mp="2pwh7pbs0c7u9">
            {title ? title : 'Case file'}
            {createdAt && <>
                {' · '}
                {new Date(createdAt).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}
              </>}
          </p>
          <h1 id="result-heading" className="mt-2 text-2xl font-semibold tracking-tight text-white sm:text-[28px]" data-id="element-220" data-mp="2dck7oxz6pubi">
            Communication pattern analysis
          </h1>
          <p className="mt-3 text-base leading-relaxed text-gray-300" data-id="element-221" data-mp="24dqdtfd8ni1n">
            {getPatternSentence(patternCount)}
          </p>
          {summary && <p className="mt-3 text-sm leading-relaxed text-forensic-muted" data-id="element-222" data-mp="1umvhtja6x430">{summary}</p>}

          <div className="mt-6 h-1 w-full overflow-hidden rounded-full bg-white/[0.06]" aria-hidden="true" data-id="element-223" data-mp="1pnsp8ybecqbx">
            <motion.div className={`h-full rounded-full ${risk.bar}`} initial={reduceMotion ? false : {
            width: 0
          }} animate={{
            width: `${clamped}%`
          }} transition={{
            duration: 0.3,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-224" data-mp="1knedb56ocw8m" />
          </div>

          <ul className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-forensic-muted" data-id="element-225" data-mp="2abs63vouljy4">
            <li className="inline-flex items-center gap-1.5" data-id="element-226" data-mp="1wkncpbc2pnw8">
              <Activity size={13} className="text-forensic-subtle" aria-hidden="true" data-id="element-227" data-mp="20ms6ca2iukiv" />
              {formatMessagesAnalyzed(messageCount)}
            </li>
            <li className="inline-flex items-center gap-1.5" data-id="element-228" data-mp="1mlxv7jo3a5gi">
              <Layers size={13} className="text-forensic-subtle" aria-hidden="true" data-id="element-229" data-mp="1rmlsuo6tpkjp" />
              {patternCount} {patternCount === 1 ? 'pattern' : 'patterns'} detected
            </li>
            <li className="inline-flex items-center gap-1.5" data-id="element-230" data-mp="1hnmkz7tvtsxr">
              <Info size={13} className="text-forensic-subtle" aria-hidden="true" data-id="element-231" data-mp="1hpzbwahmbdmy" />
              Based on communication patterns, not intent
            </li>
          </ul>
        </div>
      </div>
    </section>;
}